// Типы для авторизации
export interface LoginRequest {
    login: string;
    password: string;
    source?: string;
}

export interface RegisterRequest {
    name: string;
    surname: string;
    email: string;
    password: string;
    phone: string;
    country_code: string;
    gender: number;
    birth_date: string;
    ref: string;
    robot_type: number;
    telegram_code: string;
    terms_agreed: boolean;
    privacy_agreed: boolean;
    password_confirmation: string;
    referral_code?: string;
    marketplace_user_id?: string;
}

export interface RegisterInfoResponse {
    referrer: {
        id: number;
        name: string;
        surname: string;
        ref_link: string;
    };
    countries: Array<{
        id: number;
        name: string;
        code: string;
    }>;
    guest_id: string;
    marketplace_user?: {
        id: number;
        name: string;
        email: string;
        phone: string;
        country_code: string;
        surname: string;
        birth_date: string;
        referral_code: string;
    };
}

export interface Verify2FARequest {
    user_id: number;
    code: string;
}

export interface RegisterInfoRequest {
    ref: string;
    marketplace_user_id?: string;
}

export interface LoginResponse {
    token?: string;
    user?: {
        id: number;
        name: string;
        surname: string;
        email: string;
        mobile: string;
        inviter_id: number | null;
        telegram_id: string | null;
        bank_account: string;
        bank_swift: string;
        bank_card: string;
        bep20: string | null;
        photo: string | null;
        is_manager: boolean;
        is_blocked: boolean;
        created_at: string;
        updated_at: string;
        roles: Array<{
            id: number;
            name: string;
            guard_name: string;
            created_at: string;
            updated_at: string;
        }>;
    };
    "2fa_required"?: boolean;
    user_id?: number;
}

export interface CrossAuthRequest {
    marketplace_user_id: string;
    token: string;
}

export interface CrossAuthResponse {
    token: string;
    user: LoginResponse["user"];
}

export interface UpdateProfileRequest {
    name?: string;
    surname?: string;
    email?: string;
    phone?: string;
    bank_account?: string;
    bank_swift?: string;
    bank_card?: string;
    bep20?: string;
    photo?: File | string | null;
}

export const authEndpoints = {
    login: {
        query: (credentials: LoginRequest) => ({
            url: "/login",
            method: "POST",
            body: credentials,
        }),
    },

    loginAdminViaTelegram: {
        query: (telegramId: number) => ({
            url: "/auth/admin-telegram-webapp-login",
            method: "POST",
            body: { telegram_id: telegramId },
        }),
    },

    register: {
        query: (userData: RegisterRequest) => ({
            url: "/register",
            method: "POST",
            body: userData,
        }),
    },

    getRegisterInfo: {
        query: (params: RegisterInfoRequest) => {
            const urlParams = new URLSearchParams();
            urlParams.append("ref", params.ref);
            if (params.marketplace_user_id) {
                urlParams.append(
                    "marketplace_user_id",
                    params.marketplace_user_id
                );
            }
            return `/register?${urlParams.toString()}`;
        },
    },

    getCurrentUser: {
        query: () => "/user",
        transformResponse: (response: { data: LoginResponse["user"] }) => {
            return response.data;
        },
    },

    crossAuth: {
        query: (authData: CrossAuthRequest) => ({
            url: "/cross-auth",
            method: "POST",
            body: authData,
        }),
    },

    updateProfile: {
        queryFn: async (
            profileData: UpdateProfileRequest,
            api: any,
            _extraOptions: any,
            _baseQuery: any
        ) => {
            // Проверяем, есть ли файл для загрузки
            const hasFile = profileData.photo instanceof File;

            const state = api.getState() as any;
            const token = state.auth.token || localStorage.getItem("token");

            if (hasFile) {
                // Используем FormData для загрузки файла
                const formData = new FormData();

                Object.entries(profileData).forEach(([key, value]) => {
                    if (value !== null && value !== undefined) {
                        if (key === "photo" && value instanceof File) {
                            formData.append(key, value);
                        } else if (key !== "photo") {
                            formData.append(key, String(value));
                        }
                    }
                });

                const response = await fetch("/api/v1/user/profile", {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error("Failed to update profile");
                }

                const result = await response.json();
                return { data: result.data };
            } else {
                // Обычный JSON запрос
                const response = await fetch("/api/v1/user/profile", {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(profileData),
                });

                if (!response.ok) {
                    throw new Error("Failed to update profile");
                }

                const result = await response.json();
                return { data: result.data };
            }
        },
    },

    updateLanguage: {
        query: (language: string) => ({
            url: "/user/language",
            method: "PATCH",
            body: { language },
        }),
    },

    deletePhoto: {
        query: () => ({
            url: "/user/photo",
            method: "DELETE",
        }),
    },

    verify2FA: {
        query: (data: Verify2FARequest) => ({
            url: "/auth/verify-2fa",
            method: "POST",
            body: data,
        }),
    },
};

export const authTags = ["User"] as const;
